import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { errMessage } from "../lib/api";

const HERO =
  "https://images.pexels.com/photos/11815816/pexels-photo-11815816.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const next = searchParams.get("next") || "/dashboard";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back");
      nav(next);
    } catch (err) {
      toast.error(errMessage(err, "Login failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[--bg]">
      <div
        className="relative hidden lg:block"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(10,10,10,0.85), rgba(0,47,167,0.55)), url(${HERO})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 p-12 flex flex-col justify-between text-white">
          <div className="overline text-white/70">// Wholesale recon</div>
          <div>
            <h1 className="font-display text-5xl xl:text-6xl font-bold leading-none mb-6">
              Find the<br/>unsaturated<br/>pockets first.
            </h1>
            <p className="text-white/80 max-w-md text-base">
              Score any city or county on motivation, cash transactions, and competitor
              density. Then pull the leads with MAO, ARV and distress signals already calculated.
            </p>
          </div>
          <div className="font-mono text-xs text-white/60">v1.0 · WholesaleHQ</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <form
          onSubmit={submit}
          data-testid="login-form"
          className="w-full max-w-sm space-y-6 animate-fade-up"
        >
          <div>
            <div className="overline mb-3">Sign in</div>
            <h2 className="font-display text-3xl font-bold">Welcome back, operator.</h2>
            <p className="text-sm text-[--ink-soft] mt-2">
              No account?{" "}
              <Link to="/register" data-testid="register-link" className="text-[--accent] underline underline-offset-2">
                Create one
              </Link>
            </p>
          </div>

          <div className="space-y-3">
            <label className="block">
              <span className="text-xs font-medium block mb-1.5">Email</span>
              <input
                data-testid="login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full"
                autoComplete="email"
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium block mb-1.5">Password</span>
              <input
                data-testid="login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full"
                autoComplete="current-password"
              />
            </label>
          </div>

          <button
            type="submit"
            data-testid="login-submit-button"
            disabled={busy}
            className="btn-primary w-full"
          >
            {busy ? "Signing in…" : "Sign in →"}
          </button>

          <div className="text-xs text-[--ink-muted] font-mono pt-4 border-t border-[--line]">
            demo@wholesalehq.io · Demo@1234
          </div>
        </form>
      </div>
    </div>
  );
}
